"use strict";

var _slicedToArray = require("/agent/_work/1/s/node_modules/@babel/runtime/helpers/slicedToArray.js");

var _defineProperty = require("/agent/_work/1/s/node_modules/@babel/runtime/helpers/defineProperty.js");

Object.defineProperty(exports, "__esModule", {
  value: true
});

var react_1 = require("react");

function stateFromInitialForm(initialForm) {
  return Object.keys(initialForm).reduce(function (state, key) {
    return Object.assign(Object.assign({}, state), _defineProperty({}, key, {
      enabled: true,
      touched: false,
      dirty: false,
      errors: []
    }));
  }, {});
}

exports.stateFromInitialForm = stateFromInitialForm;

function useForceUpdate() {
  var _react_1$useState = react_1.useState(),
      _react_1$useState2 = _slicedToArray(_react_1$useState, 2),
      updateState = _react_1$useState2[1];

  return react_1.useCallback(function () {
    return updateState({});
  }, []);
}

function getDraft(draftName) {
  var serializedFormDraft = window.localStorage.getItem(draftName);

  if (serializedFormDraft) {
    return JSON.parse(serializedFormDraft);
  }

  return null;
}

function isDraftExpired(addedAtSinceEpoch, expireInMin, nowSinceEpoch) {
  var expireInMiliSeconds = expireInMin * 60 * 1000;
  return nowSinceEpoch - addedAtSinceEpoch > expireInMiliSeconds;
}

function purgeExpiredDrafts(expireInMin, nowSinceEpoch) {
  var expiredDraftKeys = Object.keys(window.localStorage).filter(function (keyName) {
    try {
      var value = JSON.parse(window.localStorage.getItem(keyName) || "{}");
      return value.addedAtSinceEpoch != undefined && isDraftExpired(value.addedAtSinceEpoch, expireInMin, nowSinceEpoch);
    } catch (_a) {
      return false;
    }
  });
  expiredDraftKeys.forEach(function (keyName) {
    return window.localStorage.removeItem(keyName);
  });
}

function compareFormField(original, latest) {
  if (Array.isArray(original) && Array.isArray(latest)) {
    if (original.length !== latest.length) {
      return "different";
    }

    var aa = original.map(function (field) {
      return field;
    }).sort();
    var bb = latest.map(function (field) {
      return field;
    }).sort();
    return aa.every(function (val, i) {
      return val === bb[i];
    }) ? "same" : "different";
  } else if (typeof original === "string" && typeof latest === "string") {
    return original === latest ? "same" : "different";
  } else if (original === undefined && (typeof latest === "string" || Array.isArray(latest))) {
    return "newFieldAdded";
  } else {
    return "different";
  }
}

exports.compareFormField = compareFormField;

function useForm(initialForm, draftInfo) {
  if (draftInfo) {
    purgeExpiredDrafts(draftInfo.draftExpireInMin, new Date().getTime());
  }

  var initialFormRef = react_1.useRef(initialForm);
  initialFormRef.current = initialForm;
  var updateForm = useForceUpdate();
  var formState = react_1.useRef(stateFromInitialForm(getFormData(initialFormRef.current)));
  var formData = react_1.useRef(getFormData(initialFormRef.current));
  var validators = react_1.useRef({});
  react_1.useEffect(function () {
    formData.current = getFormData(initialFormRef.current, draftInfo, deleteFormDraft);
    setTimeout(function () {
      return validateForm();
    });
  }, []);
  var formHasErrors = react_1.useCallback(function () {
    return !!Object.values(formState.current).find(function (state) {
      return state.errors.length > 0;
    });
  }, []);
  var setValidator = react_1.useCallback(function (name, validator) {
    validators.current[name] = validator;
  }, []);
  var setFieldValue = react_1.useCallback(function (field, value) {
    formData.current = Object.assign(Object.assign({}, formData.current), _defineProperty({}, field, value));
  }, []);
  var setMultipleFieldValues = react_1.useCallback(function (values) {
    formData.current = Object.assign(Object.assign({}, formData.current), values);
  }, []);
  var getFieldValue = react_1.useCallback(function (field) {
    return formData.current[field];
  }, []);
  var getFieldState = react_1.useCallback(function (field) {
    return formState.current[field];
  }, []);
  var setFieldState = react_1.useCallback(function (field, changes) {
    var state = getFieldState(field);
    formState.current[field] = Object.assign(Object.assign({}, state), changes);
  }, []);
  var setValue = react_1.useCallback(function (field, value, makeDirty) {
    var different = !areFieldValuesEqual(value, formData.current[field]);

    if (makeDirty && different) {
      setFieldState(field, {
        dirty: true
      });
    }

    setFieldValue(field, value);
    validateForm();
    saveFormDraft();
  }, []);
  var setMultipleValues = react_1.useCallback(function (values) {
    setMultipleFieldValues(values);
    validateForm();
    saveFormDraft();
  }, []);
  var saveFormDraft = react_1.useCallback(function () {
    if (!draftInfo) {
      return;
    }

    window.localStorage.setItem(draftInfo.draftName, JSON.stringify({
      formData: formData.current,
      addedAtSinceEpoch: new Date().getTime(),
      baseFormData: getFormData(initialFormRef.current)
    }));
    draftInfo.onDraftSaved && draftInfo.onDraftSaved();
  }, [draftInfo, formData, initialFormRef.current]);
  var deleteFormDraft = react_1.useCallback(function () {
    if (!draftInfo) {
      return;
    }

    window.localStorage.removeItem(draftInfo.draftName);
  }, []);
  var validateForm = react_1.useCallback(function () {
    for (var field in validators.current) {
      // Using a `!` here as if the key exists we can be certain the validator also exists
      var validator = validators.current[field];
      setFieldState(field, {
        errors: validator(formData.current, formState.current)
      });
    }

    updateForm();
  }, []);
  var fieldAdapter = react_1.useCallback(function (options) {
    var name = options.name,
        condition = options.condition,
        max = options.max,
        min = options.min,
        _onChange = options.onChange,
        _onBlur = options.onBlur,
        required = options.required;
    var validator = buildFieldValidator(options);
    setFieldState(name, {
      enabled: condition === undefined ? true : condition,
      errors: validator(formData.current, formState.current)
    });

    var _getFieldState = getFieldState(name),
        touched = _getFieldState.touched,
        dirty = _getFieldState.dirty,
        errors = _getFieldState.errors;

    var value = getFieldValue(name);
    setValidator(name, validator);
    return {
      name: name,
      value: value,
      required: required ? true : false,
      touched: touched,
      dirty: dirty,
      error: errors.length > 0 ? errors[0] : undefined,
      hide: condition === undefined ? false : !condition,
      min: min ? min[0] : undefined,
      max: max ? max[0] : undefined,
      onChange: function onChange(e) {
        var fieldValue = deriveInputValue(e.currentTarget, value);

        if (_onChange) {
          _onChange(fieldValue);
        }

        setFieldValue(name, fieldValue);

        if (!areFieldValuesEqual(value, fieldValue)) {
          setFieldState(name, {
            dirty: true
          });
        }

        validateForm();
      },
      onBlur: function onBlur(e) {
        var fieldValue = deriveInputValue(e.currentTarget, value);

        if (_onBlur) {
          _onBlur(fieldValue);
        }

        setFieldState(name, {
          touched: true
        });
        validateForm();
        saveFormDraft();
      }
    };
  }, []);
  var submitAdapter = react_1.useCallback(function (callback) {
    return function (e) {
      e === null || e === void 0 ? void 0 : e.preventDefault();
      var errorKeys = Object.keys(formState.current).filter(function (k) {
        var state = formState.current[k];
        return state && state.errors.length > 0;
      });
      var hasErrors = formHasErrors();
      var conditionalFormData = Object.keys(formData.current).reduce(function (result, field) {
        var enabled = formState.current[field].enabled;
        var value = formData.current[field];
        return Object.assign(Object.assign({}, result), _defineProperty({}, field, enabled ? value : Array.isArray(value) ? [] : ""));
      }, formData.current);

      if (hasErrors) {
        Object.keys(formState.current).forEach(function (field) {
          return setFieldState(field, {
            touched: true
          });
        });
        validateForm();
        setTimeout(function () {
          var firstField = document.querySelector("[name=".concat(errorKeys[0], "]"));

          if (firstField) {
            firstField.focus();
          }
        });
      }

      return callback(!hasErrors, conditionalFormData, formState.current);
    };
  }, []);
  var resetForm = react_1.useCallback(function () {
    formData.current = getFormData(initialFormRef.current, draftInfo, deleteFormDraft);
    Object.keys(formState.current).forEach(function (field) {
      return setFieldState(field, {
        touched: false
      });
    });
    updateForm();
  }, []);
  return {
    fieldAdapter: fieldAdapter,
    formData: formData.current,
    formState: formState.current,
    setValue: setValue,
    setMultipleValues: setMultipleValues,
    submitAdapter: submitAdapter,
    formHasErrors: formHasErrors,
    resetForm: resetForm,
    deleteFormDraft: deleteFormDraft
  };
}

exports.useForm = useForm;

function getFormData(initialForm, draftInfo, onDiscardDraftCallback) {
  var serverFormData = typeof initialForm !== "function" ? initialForm : initialForm();
  var resultFormData = Object.assign({}, serverFormData);

  if (draftInfo) {
    var formDraft = getDraft(draftInfo.draftName);

    if (formDraft && formDraft.baseFormData) {
      for (var fieldKey in serverFormData) {
        var comparisonResult = compareFormField(formDraft.baseFormData[fieldKey], serverFormData[fieldKey]);

        switch (comparisonResult) {
          case "same":
            resultFormData[fieldKey] = formDraft.formData[fieldKey];
            break;

          case "different":
            alert("Your draft is out of date, and it will not be used");
            onDiscardDraftCallback && onDiscardDraftCallback();
            return serverFormData;

          case "newFieldAdded":
            break;
        }
      }
    }
  }

  return resultFormData;
}

function buildFieldValidator(_ref) {
  var name = _ref.name,
      condition = _ref.condition,
      required = _ref.required,
      min = _ref.min,
      max = _ref.max,
      minLength = _ref.minLength,
      maxLength = _ref.maxLength,
      pattern = _ref.pattern,
      validate = _ref.validate;
  return function (formData, formState) {
    if (condition === false) {
      return [];
    }

    var value = formData[name];
    var errors = [];

    if (!isSingleValue(value)) {
      if (required && value.length === 0) {
        errors.push(required === true ? "" : required);
      }
    } else {
      if (value) {
        if (min && +value < min[0]) {
          errors.push(min[1] || "");
        }

        if (max && +value > max[0]) {
          errors.push(max[1] || "");
        }

        if (minLength && value.length < minLength[0]) {
          errors.push(minLength[1] || "");
        }

        if (maxLength && value.length > maxLength[0]) {
          errors.push(maxLength[1] || "");
        }

        if (pattern && !value.match(pattern[0])) {
          errors.push(pattern[1] || "");
        }
      } else if (required) {
        errors.push(required === true ? "" : required);
      }

      if (validate) {
        var isValid = validate(value, formData, formState);

        if (isValid !== true) {
          errors.push(isValid);
        }
      }
    }

    return errors;
  };
}

function deriveInputValue(input, value) {
  if (Array.isArray(value)) {
    var result = value.filter(function (v) {
      return v !== input.value;
    });

    if (input.checked) {
      result.push(input.value);
    }

    return result;
  }

  return input.value;
}

function areFieldValuesEqual(values, fieldValues) {
  if (isSingleValue(values) && isSingleValue(fieldValues)) {
    return values === fieldValues;
  }

  if (!isSingleValue(values) && !isSingleValue(fieldValues)) {
    return values.length === fieldValues.length && values.every(function (element, index) {
      return element === fieldValues[index];
    });
  }

  return false;
}

function isSingleValue(value) {
  return !Array.isArray(value);
}